import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Card, CardMedia, Dialog } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { QRCodeSVG } from 'qrcode.react';

function SavedItems() {
  const navigate = useNavigate();
  const [savedItems, setSavedItems] = useState([]);
  const [showCommessoDialog, setShowCommessoDialog] = useState(false);
  const [commessoCode, setCommessoCode] = useState('');

  useEffect(() => {
    loadSavedItems();
  }, []);

  const loadSavedItems = () => {
    const items = JSON.parse(localStorage.getItem('savedItems') || '[]');
    setSavedItems(items);
  };

  const removeItem = (productId) => {
    const updated = savedItems.filter(item => item.id !== productId);
    localStorage.setItem('savedItems', JSON.stringify(updated));
    setSavedItems(updated);
  };

  const clearAll = () => {
    localStorage.setItem('savedItems', JSON.stringify([]));
    setSavedItems([]);
  };

  const generateCommessoCode = () => {
    // Genera un codice univoco per il commesso
    const itemIds = savedItems.map(item => item.id).join(',');
    const sessionId = Date.now().toString(36);
    const code = `${sessionId}-${itemIds}`;

    // In produzione, questo codice verrebbe salvato su Firebase
    // e il commesso potrebbe scannerizzarlo per vedere i prodotti

    setCommessoCode(code);
    setShowCommessoDialog(true);
  };

  const calculateTotal = () => {
    return savedItems.reduce((total, item) => total + item.price, 0).toFixed(2);
  };

  if (savedItems.length === 0) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            backgroundColor: '#ffffff'
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: 'rgb(51, 51, 51)' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: '#000'
            }}
          >
            Capi Salvati
          </Typography>
          <Box sx={{ width: '40px' }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px'
          }}
        >
          <Typography
            sx={{
              fontSize: '48px',
              mb: 2
            }}
          >
            🛍️
          </Typography>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#000',
              mb: 1,
              textAlign: 'center'
            }}
          >
            Nessun capo salvato
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#666',
              textAlign: 'center',
              mb: 4
            }}
          >
            Scansiona un QR code per provare i capi virtualmente
          </Typography>
          <Button
            variant="contained"
            startIcon={<QrCodeScannerIcon />}
            onClick={() => navigate('/')}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              textTransform: 'none',
              padding: '12px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#333'
              }
            }}
          >
            Scansiona QR Code
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ color: 'rgb(51, 51, 51)' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            color: '#000'
          }}
        >
          I Tuoi Capi ({savedItems.length})
        </Typography>
        <Button
          onClick={clearAll}
          sx={{
            textTransform: 'none',
            color: '#f44336',
            fontSize: '14px'
          }}
        >
          Cancella
        </Button>
      </Box>

      {/* Items List */}
      <Box
        sx={{
          flex: 1,
          padding: '16px',
          overflow: 'auto'
        }}
      >
        {savedItems.map((item) => (
          <Card
            key={item.id}
            sx={{
              mb: 2,
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{ display: 'flex', p: { xs: 1.5, sm: 2 } }}>
              <CardMedia
                component="img"
                sx={{
                  width: { xs: 70, sm: 80 },
                  height: { xs: 90, sm: 100 },
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
                image={item.tryOnImage || item.imageUrl}
                alt={item.name}
              />
              <Box sx={{ flex: 1, ml: { xs: 1.5, sm: 2 } }}>
                <Typography
                  sx={{
                    fontSize: { xs: '14px', sm: '16px' },
                    fontWeight: 600,
                    color: '#000',
                    mb: 0.5
                  }}
                >
                  {item.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '14px' },
                    color: '#666',
                    mb: 1
                  }}
                >
                  {item.category}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '16px', sm: '18px' },
                    fontWeight: 700,
                    color: '#000'
                  }}
                >
                  €{item.price}
                </Typography>
              </Box>
              <IconButton
                onClick={() => removeItem(item.id)}
                sx={{
                  alignSelf: 'flex-start',
                  color: '#999'
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Bottom Actions */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          padding: '24px',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            sx={{
              fontSize: '16px',
              color: '#666'
            }}
          >
            Totale stimato
          </Typography>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#000'
            }}
          >
            €{calculateTotal()}
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={generateCommessoCode}
          sx={{
            backgroundColor: '#4CAF50',
            color: '#fff',
            textTransform: 'none',
            padding: '16px',
            fontSize: '16px',
            fontWeight: 600,
            borderRadius: '12px',
            mb: 2,
            '&:hover': {
              backgroundColor: '#45a049'
            }
          }}
        >
          Mostra al commesso 🛍️
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => navigate('/')}
          sx={{
            borderColor: '#000',
            color: '#000',
            textTransform: 'none',
            padding: '12px',
            fontSize: '16px',
            borderRadius: '12px',
            '&:hover': {
              borderColor: '#000',
              backgroundColor: 'rgba(0,0,0,0.05)'
            }
          }}
        >
          Continua a provare
        </Button>
      </Box>

      {/* Commesso Dialog */}
      <Dialog
        open={showCommessoDialog}
        onClose={() => setShowCommessoDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          sx={{
            padding: '32px',
            textAlign: 'center'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2
            }}
          >
            Mostra questo al commesso
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#666',
              mb: 3
            }}
          >
            Il commesso può scansionare questo QR per vedere i capi che hai scelto
          </Typography>

          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '16px',
              border: '2px solid #000',
              mb: 3
            }}
          >
            <QRCodeSVG
              value={`PROVAMI_SESSION:${commessoCode}`}
              size={200}
              level="H"
              includeMargin={true}
            />
          </Box>

          <Typography
            sx={{
              fontSize: '12px',
              color: '#999',
              mb: 3
            }}
          >
            Codice: {commessoCode.split('-')[0]}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => setShowCommessoDialog(false)}
            sx={{
              backgroundColor: '#000',
              textTransform: 'none',
              padding: '12px',
              borderRadius: '12px'
            }}
          >
            Chiudi
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}

export default SavedItems;
